import { useEffect } from "react";

const SITE_URL = "https://athenacrestdefense.com";
const BRAND_NAME = "AthenaCrest Defense";
const DEFAULT_TITLE = `${BRAND_NAME} | Healthcare Operations & Compliance Consulting · St. Louis, MO`;
const DEFAULT_DESCRIPTION =
  "St. Louis-based healthcare operations, compliance, and risk consulting for skilled nursing facilities and long-term care organizations across Missouri. Founded by Sierra Thomas, MJ Health Law & Compliance Studies.";
const DEFAULT_IMAGE = `${SITE_URL}/images/logo.png`;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * Updates document <head> with per-page SEO tags + optional JSON-LD.
 *
 * @param {Object} opts
 * @param {string} [opts.title] - page-specific title (without brand suffix)
 * @param {string} [opts.description] - meta description
 * @param {string} [opts.path] - canonical path (default: window.location.pathname)
 * @param {string} [opts.image] - OG image URL
 * @param {Object|Array} [opts.jsonLd] - structured data for the page
 */
export function useSEO({ title, description, path, image, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${BRAND_NAME}` : DEFAULT_TITLE;
    document.title = fullTitle;

    const desc = description || DEFAULT_DESCRIPTION;
    const img = image || DEFAULT_IMAGE;
    const url = `${SITE_URL}${path || window.location.pathname}`;

    setMeta("description", desc);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", img, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", BRAND_NAME, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", img);

    setCanonical(url);

    // Per-page JSON-LD (cleaned on every navigation)
    const existing = document.head.querySelector(
      'script[data-seo="page-jsonld"]'
    );
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "page-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, JSON.stringify(jsonLd || null)]);
}

export const SEO = { SITE_URL, BRAND_NAME };
